import { useState } from "react";
import { toast } from "sonner";
import { PageHeader } from "../components/PageHeader";
import { FadeUp, Overline } from "../components/Reveal";
import { SERVICES } from "../data/services";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { MapPin, Envelope, Phone } from "@phosphor-icons/react";

const inputCls = "h-12 bg-muted/50 border-primary/10 focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", organization: "", service: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Enquiry sent — our team will reach out within one business day.");
      setForm({ name: "", email: "", organization: "", service: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageHeader
        testId="contact-page"
        overline="Contact"
        title={["Tell us where you", "want to go."]}
        subtitle="Whether it's a single pipeline or a hospital-wide transformation, the conversation starts here."
      />
      <section className="pb-24 lg:pb-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-4 space-y-10">
            <FadeUp>
              <Overline>Reach us directly</Overline>
              <div className="mt-8 space-y-6">
                <div className="flex items-start gap-4">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/5 text-primary"><Envelope size={20} weight="duotone" /></span>
                  <div>
                    <p className="text-sm font-semibold text-foreground">Email</p>
                    <p className="text-sm text-muted-foreground">contact[at]anxya.tech</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/5 text-primary"><Phone size={20} weight="duotone" /></span>
                  <div>
                    <p className="text-sm font-semibold text-foreground">Phone</p>
                    <p className="text-sm text-muted-foreground">+91-6398009260</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/5 text-primary"><MapPin size={20} weight="duotone" /></span>
                  <div>
                    <p className="text-sm font-semibold text-foreground">Offices</p>
                    <p className="text-sm text-muted-foreground">Pune (HQ) · Mumbai</p>
                  </div>
                </div>
              </div>
            </FadeUp>
          </div>

          <FadeUp delay={0.15} className="lg:col-span-8">
            <form onSubmit={submit} data-testid="contact-form" className="rounded-3xl border border-primary/10 bg-white p-8 lg:p-12 space-y-6">
              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <label className="text-sm font-medium text-foreground" htmlFor="contact-name">Full name *</label>
                  <Input id="contact-name" data-testid="contact-name-input" required value={form.name} onChange={set("name")} className={`mt-2 ${inputCls}`} placeholder="Dr. Asha Verma" />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground" htmlFor="contact-email">Work email *</label>
                  <Input id="contact-email" data-testid="contact-email-input" required type="email" value={form.email} onChange={set("email")} className={`mt-2 ${inputCls}`} placeholder="asha@hospital.org" />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground" htmlFor="contact-org">Organization</label>
                  <Input id="contact-org" data-testid="contact-org-input" value={form.organization} onChange={set("organization")} className={`mt-2 ${inputCls}`} placeholder="City General Hospital" />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground" htmlFor="contact-service">Area of interest</label>
                  <select
                    id="contact-service"
                    data-testid="contact-service-select"
                    value={form.service}
                    onChange={set("service")}
                    className={`mt-2 w-full rounded-md border px-3 text-sm ${inputCls}`}
                  >
                    <option value="">Select a service area</option>
                    {SERVICES.map((s) => <option key={s.slug} value={s.name}>{s.name}</option>)}
                    <option value="Other">Other / Not sure yet</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-foreground" htmlFor="contact-message">Message *</label>
                <Textarea id="contact-message" data-testid="contact-message-input" required rows={5} value={form.message} onChange={set("message")} className="mt-2 bg-muted/50 border-primary/10 focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2" placeholder="Tell us about your goals, timelines, and current systems…" />
              </div>
              <Button
                type="submit"
                data-testid="contact-submit-btn"
                disabled={submitting}
                className="rounded-full bg-primary px-8 h-12 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-accent/20"
              >
                {submitting ? "Sending…" : "Send Enquiry"}
              </Button>
            </form>
          </FadeUp>
        </div>
      </section>
    </>
  );
}
